//
// AHT Weight Management — Shared Assessment (Phase 1 Gate, Initial Consultations Only)
// Source: AHT Shared Assessment
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// Drug-specific exclusions are NOT assessed here.

import type { Pathway, IOTFLookupNode } from "../../types/pathway.types";
import { WEIGHT_ASSESSMENT_STEP_TOTAL } from "./_shared/weightFlowSteps";
import {
  getPatientEthnicAdjustmentGroup,
  isEthnicAdjustmentGroup,
} from "./weight-selection.pathway";

function patientInAdjustedEthnicGroup(patient: unknown): boolean {
  return isEthnicAdjustmentGroup(getPatientEthnicAdjustmentGroup(patient));
}

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const SA_Q0_GATEWAY              = "sa-q0-gateway";

// Inclusion checks
const SA_Q1_AGE                      = "sa-q1-age";
const SA_Q2_BMI_CAPTURE              = "sa-q2-bmi-capture";
const SA_Q3_CONSENT                  = "sa-q3-consent";      // entry point: commitments checklist
const SA_Q3_COMMITMENTS_CHECKLIST    = "sa-q3-commitments-checklist";

// Exclusion checks
const SA_Q7_BMI_FLOOR                = "sa-q7-bmi-floor";    // entry point: exclusions checklist
const SA_Q7_EXCLUSIONS_CHECKLIST     = "sa-q7-exclusions-checklist";

// Red flags alarm node
const SA_RED_FLAGS_ALARM      = "sa-red-flags-alarm";

// Stop-screen wording, split per red flag. `body` below is composed from these so it stays the full text.
const RED_FLAG_STOP_INTRO =
  "Refer urgently / manage acute illness first · do not proceed with weight management today.";
const RED_FLAG_STOP_OUTRO = "Document the red flag identified and the action taken.";
const RED_FLAG_STOP_ACTIONS: { id: string; lines: string[] }[] = [
  { id: "rf-systemically-unwell", lines: ["Calculate NEWS2 and refer urgently."] },
  { id: "rf-life-threatening", lines: ["Call 999."] },
  { id: "rf-acute-illness", lines: ["Manage acute illness first."] },
];

// Adolescent branch
const SA_Q1A_WEIGHT           = "sa-q1a-weight";
const SA_Q1A_PARENTAL         = "sa-q1a-parental";
const SA_Q2_BMI_CONFIRM              = "sa-q2-bmi-confirm";
const SA_Q2_BMI_CONFIRM_MANUAL       = "sa-q2-bmi-confirm-manual";
const SA_Q2_BMI_ADOLESCENT           = "sa-q2-bmi-adolescent";
const SA_Q2_BMI_INCL_ADOLESCENT_NEXT = "sa-q2-bmi-incl-adolescent-next";

// Outcomes
const OUT_INCLUSION_NOT_MET   = "OUT_INCLUSION_NOT_MET";
const OUT_EXCLUSION_TRIGGERED = "OUT_EXCLUSION_TRIGGERED";
const OUT_CLEARED             = "OUT_CLEARED";
const OUT_UNDER12             = "OUT_UNDER12";
const OUT_OVER84              = "OUT_OVER84";
const OUT_BMI_BELOW_THRESHOLD = "OUT_BMI_BELOW_THRESHOLD";
const OUT_BMI_IOTF_BELOW      = "OUT_BMI_IOTF_BELOW";
const OUT_WEIGHT_BELOW_60KG   = "OUT_WEIGHT_BELOW_60KG";
const OUT_CONSENT_REFUSED     = "OUT_CONSENT_REFUSED";
const OUT_MONITORING_REFUSED  = "OUT_MONITORING_REFUSED";
const OUT_LIFESTYLE_DECLINED       = "OUT_LIFESTYLE_DECLINED";
const OUT_COMMITMENTS_NOT_MET      = "OUT_COMMITMENTS_NOT_MET";

// Re-initiation branch (returning patients — replaces the Q2 BMI gate)
const SA_REINIT_ROUTE       = "sa-reinit-consultation-type-route";
const SA_REINIT_Q1          = "sa-reinit-q1-original-bmi";
const SA_REINIT_Q2          = "sa-reinit-q2-weight-loss-response";
const SA_REINIT_Q3          = "sa-reinit-q3-within-12-months";
const SA_REINIT_Q4          = "sa-reinit-q4-weight-regain";
const SA_REINIT_BMI_FLOOR   = "sa-reinit-bmi-floor-check";
const OUT_REINIT_CANNOT     = "OUT_REINIT_CANNOT_PROCEED";
const OUT_REINIT_OVER12M    = "OUT_REINIT_OVER_12_MONTHS";

// Transfer branch (BUG6). A patient still taking the medicine is not turned away at the BMI step.
// Source: change list BUG6 (29/10/2026), Mounjaro, Wegovy, Mysimba and Orlistat PGD v2.0 Exclusion criteria.
const SA_TRANS_MEDICINE      = "sa-trans-medicine";
const SA_TRANS_EVIDENCE      = "sa-trans-evidence";
const SA_TRANS_EVIDENCE_OTHER = "sa-trans-evidence-other";
const SA_TRANS_BASELINE_BMI   = "sa-trans-baseline-bmi";
const SA_TRANS_LAST_DOSE      = "sa-trans-last-dose-date";
const SA_TRANS_START_ROUTE    = "sa-trans-start-bmi-route";
const SA_TRANS_BELOW_FLAG     = "sa-trans-below-start-flag";
const SA_TRANS_FLOOR_ROUTE    = "sa-trans-floor-route";
const SA_TRANS_DECISION       = "sa-trans-continue-decision";
const SA_TRANS_DECISION_REASON = "sa-trans-continue-decision-reason";
const OUT_TRANS_NO_EVIDENCE   = "OUT_TRANS_NO_EVIDENCE";
const OUT_TRANS_BELOW_FLOOR   = "OUT_TRANS_BMI_BELOW_FLOOR";
const OUT_TRANS_STOPPED       = "OUT_TRANS_DECISION_STOP";

/** Starting BMI for a transfer medicine: 28 for orlistat, 27 for the rest. */
export const transferStartingBmi = (drugId: unknown): number => (drugId === "orlistat" ? 28 : 27);
/** Continuing-treatment BMI floor: 22 tirzepatide, 22.5 other GLP-1 type medicines, none for Mysimba and orlistat. */
export const transferBmiFloor = (drugId: unknown): number | null =>
  drugId === "tirzepatide" ? 22 : drugId === "mysimba" || drugId === "orlistat" ? null : 22.5;

const SA_EXCLUSION_LABELS: [string, string][] = [
  ["sa-q7a-bariatric-surgery", "Prior bariatric or metabolic surgery (e.g. gastric bypass, sleeve gastrectomy, gastric band, duodenal switch)"],
  ["sa-q8-pregnancy", "Patient is pregnant, breastfeeding, or planning pregnancy"],
  ["sa-q9-concurrent", "Patient is currently using another weight loss medicine or GLP-1 receptor agonist for any indication"],
  ["sa-q10-endocrine", "Obesity is secondary to an endocrine condition currently under specialist management"],
  ["sa-q11-monitoring-refusal", "Patient is refusing BMI monitoring or required follow-up reviews"],
];

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtSharedAssessmentPathway: Pathway = {
  id: "aht-wt-shared-assessment",
  name: "AHT Weight Management",
  population: "Adults presenting for initial weight management consultation",
  // The consultation type question is step 1, so this pathway's first screen is step 2.
  refinedFlowUi: { stepOffset: 1, stepTotal: WEIGHT_ASSESSMENT_STEP_TOTAL },
  pgdRefs: [],
  demographicConfiguration: { bannerFields: [] },
  entryNode: SA_Q0_GATEWAY,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────────
    // Step 1 — Red flags checklist
    // ─────────────────────────────────────────────────────────────────────────────
    {
      id: SA_Q0_GATEWAY,
      type: "checklist",
      text: "Check for red flags",
      hint: "Select any red flags present. If none apply, select ‘None of the above’ and continue.",
      items: [
        {
          id: "rf-systemically-unwell",
          text: "Patient is systemically unwell",
        },
        {
          id: "rf-life-threatening",
          text: "Presentation is life-threatening",
        },
        {
          id: "rf-acute-illness",
          text: "Acute illness that makes starting weight management medicine inappropriate at this time",
        },
      ],
      includeNoneOption: true,
      anySelected: SA_RED_FLAGS_ALARM,
      noneSelected: SA_Q1_AGE,
    },

    // Red flags alarm — terminates consultation, offers NEWS2 calculator
    {
      id: SA_RED_FLAGS_ALARM,
      type: "gateway_alarm",
      heading: "Red flag present — do not proceed with weight management consultation",
      body: `${RED_FLAG_STOP_INTRO}\n\n${RED_FLAG_STOP_ACTIONS.flatMap((f) => f.lines).join("\n")}\n${RED_FLAG_STOP_OUTRO}`,
      flagActions: {
        sourceNodeId: SA_Q0_GATEWAY,
        intro: RED_FLAG_STOP_INTRO,
        outro: RED_FLAG_STOP_OUTRO,
        flags: RED_FLAG_STOP_ACTIONS,
      },
      requireActionTaken: true,
      linkedCalculatorId: "news2",
      linkedCalculatorLabel: "Calculate NEWS2 score",
      action: "close",
      triggersPayment: false,
      confirmBeforeClose: true,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 2 — Shared inclusion criteria (all must be Yes)
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: SA_Q1_AGE,
      type: "condition_route",
      conditions: [
        { when: { patientAgeUnder: 12 },                             then: { outcomeId: OUT_UNDER12 } },
        { when: { patientAgeAtLeast: 12, patientAgeUnder: 18 },      then: SA_Q1A_WEIGHT },
        { when: { patientAgeAtLeast: 18, patientAgeUnder: 85 },      then: SA_REINIT_ROUTE },
        { when: { patientAgeAtLeast: 85 },                           then: { outcomeId: OUT_OVER84 } },
      ],
      default: { outcomeId: OUT_INCLUSION_NOT_MET },
    },

    // Adolescent branch — weight check (>60 kg required for liraglutide)
    {
      id: SA_Q1A_WEIGHT,
      type: "yes_no",
      text: "Does the patient weigh more than 60 kg?",
      hint: "Liraglutide in adolescents (12–17) requires body weight above 60 kg. If below 60 kg, liraglutide is not eligible under this PGD.",
      storesTo: "sharedAdolescentWeightEligible",
      answerPolarity: "yes-positive",
      yes: SA_Q2_BMI_CAPTURE,
      no: { outcomeId: OUT_WEIGHT_BELOW_60KG },
    },

    // Adolescent BMI — IOTF lookup
    {
      id: SA_Q2_BMI_ADOLESCENT,
      type: "iotf_lookup",
      iotfTableId: "iotf-cutoffs-v1",
      meetsThreshold: SA_Q1A_PARENTAL,
      belowThreshold: { outcomeId: OUT_BMI_IOTF_BELOW },
      pendingDataOutcome: { outcomeId: OUT_BMI_IOTF_BELOW },
      sexNotResolvable: { outcomeId: OUT_BMI_IOTF_BELOW },
    } as IOTFLookupNode,

    // Adolescent parental involvement — soft gate, not a hard block
    {
      id: SA_Q1A_PARENTAL,
      type: "yes_no",
      text: "Has parent or guardian involvement been documented? (Strongly encouraged for ages 12–15 — not a hard clinical gate)",
      hint: "Document parental or guardian involvement where applicable. This is strongly encouraged for patients aged 12–15 but does not block the pathway if not possible.",
      storesTo: "sharedParentalInvolvement",
      answerPolarity: "neutral",
      yes: SA_Q3_CONSENT,
      no: SA_Q3_CONSENT,
    },

    // Re-initiation branch — returning patients skip the BMI inclusion
    // threshold (SA_Q2_BMI_CONFIRM onward) and instead confirm the
    // re-initiation attestation criteria below, then record BMI and pass the
    // drug-specific floor. New patients continue to the standard BMI gate.
    {
      id: SA_REINIT_ROUTE,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) =>
            ctx.context["aht-sel-consultation-type"] === "reinitiation",
          then: SA_REINIT_Q1,
        },
      ],
      default: SA_Q2_BMI_CAPTURE,
    },

    {
      id: SA_REINIT_Q1,
      type: "yes_no",
      text: "Is the patient's original BMI documented and confirmed as having met the eligibility criteria at the time of their initial supply?",
      hint: "The clinician must record the original (initial) BMI. Re-initiation requires documented evidence that the patient met the BMI threshold at first initiation.",
      answerPolarity: "yes-positive",
      yes: SA_REINIT_Q2,
      no: { outcomeId: OUT_REINIT_CANNOT },
    },

    {
      id: SA_REINIT_Q2,
      type: "yes_no",
      text: "Is there documented evidence of at least 5% weight loss achieved on their previous course of this treatment?",
      hint: "A documented treatment response of ≥5% weight loss is required for re-initiation. This must be recorded.",
      answerPolarity: "yes-positive",
      yes: SA_REINIT_Q3,
      no: { outcomeId: OUT_REINIT_CANNOT },
    },

    {
      id: SA_REINIT_Q3,
      type: "yes_no",
      text: "Is the patient presenting within 12 months of their last dose of this treatment?",
      hint: "Re-initiation is only permitted within 12 months of the last dose. If more than 12 months have elapsed, the patient must satisfy the inclusion criteria afresh and be treated as a new initiation — start an Initial Supply consultation instead.",
      answerPolarity: "yes-positive",
      yes: SA_REINIT_Q4,
      no: { outcomeId: OUT_REINIT_OVER12M },
    },

    {
      id: SA_REINIT_Q4,
      type: "yes_no",
      text: "Is there documented weight regain since stopping treatment?",
      hint: "Re-initiation requires documented weight regain since the patient stopped treatment.",
      answerPolarity: "yes-positive",
      // BMI is captured fresh (SA_Q2_BMI_CAPTURE) before the floor check; SA_Q2_BMI_INCL_ADOLESCENT_NEXT sends re-initiation to SA_REINIT_BMI_FLOOR.
      yes: SA_Q2_BMI_CAPTURE,
      no: { outcomeId: OUT_REINIT_CANNOT },
    },

    // Drug-specific BMI floor check.
    // Source: Mounjaro PGD v1.6 (floor 22 kg/m²),
    //         Wegovy injectable PGD v1.6, Wegovy oral PGD v1.1,
    //         Liraglutide PGD v1.1 (all floor 22.5 kg/m²).
    // The re-initiation drug was stored as aht-sel-reinitiation-drug
    // by the drug-select pathway. BMI is in ctx.context.bmi, captured at
    // SA_Q2_BMI_CAPTURE immediately before this check.
    {
      id: SA_REINIT_BMI_FLOOR,
      type: "condition_route",
      conditions: [
        {
          // Fail safe: a missing or non-numeric BMI cannot be shown to meet the floor.
          evaluate: (ctx) =>
            !(
              typeof ctx.context.bmi === "number" &&
              Number.isFinite(ctx.context.bmi) &&
              ctx.context.bmi > 0
            ),
          then: { outcomeId: OUT_REINIT_CANNOT },
        },
        {
          // Tirzepatide floor: BMI must be ≥22 kg/m²
          evaluate: (ctx) =>
            ctx.context["aht-sel-reinitiation-drug"] === "tirzepatide" &&
            Number(ctx.context.bmi) < 22,
          then: { outcomeId: OUT_REINIT_CANNOT },
        },
        {
          // All other drugs (semaglutide, oral_semaglutide, liraglutide):
          // floor 22.5 kg/m²
          evaluate: (ctx) =>
            ctx.context["aht-sel-reinitiation-drug"] !== "tirzepatide" &&
            Number(ctx.context.bmi) < 22.5,
          then: { outcomeId: OUT_REINIT_CANNOT },
        },
      ],
      // Floor met — proceed to shared monitoring/consent section (same entry
      // point the standard Q2 BMI gate leads to: SA_Q3_CONSENT).
      default: SA_Q3_CONSENT,
    },

    // Combined height + weight + BMI capture
    {
      id: SA_Q2_BMI_CAPTURE,
      type: "bmi_capture",
      text: "Patient Measurements",
      hint: "Record the patient's height and weight. BMI will be calculated automatically.",
      heightConfig: {
        hardMin: 50,
        hardMax: 250,
        decimals: 1,
        plausibilityBands: [
          { min: 120, max: 220, tier: "silent_accept" as const },
          { min: 50, max: 119, tier: "confirmation_prompt" as const, message: "Height below 120 cm is unusual for an adult. Please confirm." },
          { min: 221, max: 250, tier: "confirmation_prompt" as const, message: "Height above 220 cm is unusual. Please confirm." },
        ],
      },
      weightConfig: {
        hardMin: 20,
        hardMax: 350,
        decimals: 1,
        plausibilityBands: [
          { min: 30, max: 300, tier: "silent_accept" as const },
          { min: 20, max: 29, tier: "confirmation_prompt" as const, message: "Weight below 30 kg is unusual for an adult. Please confirm." },
          { min: 301, max: 350, tier: "confirmation_prompt" as const, message: "Weight above 300 kg is unusual. Please confirm." },
        ],
      },
      storesToHeight: "heightCm",
      storesToWeight: "weightKg",
      storesToBmi: "bmi",
      next: SA_Q2_BMI_INCL_ADOLESCENT_NEXT,
    } as any,

    // Routes adults re-initiating to the BMI floor check, adolescents to IOTF lookup, other adults to BMI threshold confirmation
    {
      id: SA_Q2_BMI_INCL_ADOLESCENT_NEXT,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) =>
            ctx.context["aht-sel-consultation-type"] === "reinitiation" &&
            ctx.patient.ageYears >= 18,
          then: SA_REINIT_BMI_FLOOR,
        },
        {
          evaluate: (ctx) =>
            ctx.context["aht-sel-consultation-type"] === "transfer" &&
            ctx.patient.ageYears >= 18,
          then: SA_TRANS_MEDICINE,
        },
        {
          when: { patientAgeAtLeast: 12, patientAgeUnder: 18 },
          then: SA_Q2_BMI_ADOLESCENT,
        },
      ],
      default: SA_Q2_BMI_CONFIRM,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Transfer branch — which medicine, evidence, baseline BMI, last dose, then the
    // starting-BMI and floor checks. Only this medicine stays eligible downstream.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: SA_TRANS_MEDICINE,
      type: "single_select",
      text: "Which medicine is the patient currently taking?",
      hint: "Only this medicine stays eligible for the rest of the consultation.",
      options: [
        { id: "tirzepatide", label: "Tirzepatide (Mounjaro®)" },
        { id: "semaglutide", label: "Semaglutide injection (Wegovy®)" },
        { id: "liraglutide", label: "Liraglutide (Saxenda® / Biolide®)" },
        { id: "foundayo", label: "Orforglipron hard capsules (changing to Foundayo® tablets)" },
        { id: "mysimba", label: "Mysimba® (naltrexone/bupropion)" },
        { id: "orlistat", label: "Orlistat (Xenical®)" },
      ],
      routing: {
        tirzepatide: SA_TRANS_EVIDENCE,
        semaglutide: SA_TRANS_EVIDENCE,
        liraglutide: SA_TRANS_EVIDENCE,
        foundayo: SA_TRANS_EVIDENCE,
        mysimba: SA_TRANS_EVIDENCE,
        orlistat: SA_TRANS_EVIDENCE,
      },
      storesTo: "aht-sel-transfer-drug",
      required: true,
    },

    {
      id: SA_TRANS_EVIDENCE,
      type: "multi_select",
      text: "Evidence of current treatment seen by the clinician. Select all that apply.",
      hint: "At least one is needed. An upload is optional.",
      items: [
        { id: "trans_ev_supply_record", text: "Previous supply record" },
        { id: "trans_ev_prescription", text: "Prescription" },
        { id: "trans_ev_dispensing_label", text: "Dispensing label" },
        { id: "trans_ev_pack_or_pen", text: "Patient's medicine pack or pen" },
        { id: "trans_ev_other", text: "Other" },
      ],
      noneOption: { label: "No evidence seen" },
      storesTo: "sharedTransferEvidence",
      routing: (selections) =>
        selections.length === 0
          ? { outcomeId: OUT_TRANS_NO_EVIDENCE }
          : selections.includes("trans_ev_other")
            ? SA_TRANS_EVIDENCE_OTHER
            : SA_TRANS_BASELINE_BMI,
    },

    {
      id: SA_TRANS_EVIDENCE_OTHER,
      type: "free_text",
      text: "Describe the other evidence seen",
      required: true,
      minLength: 3,
      storesTo: "sharedTransferEvidenceOther",
      next: SA_TRANS_BASELINE_BMI,
    },

    {
      id: SA_TRANS_BASELINE_BMI,
      type: "numeric_input",
      text: "BMI when this medicine was first started",
      unit: "kg/m²",
      decimals: 1,
      hardMin: 10,
      hardMax: 100,
      storesTo: "sharedTransferBaselineBmi",
      next: SA_TRANS_LAST_DOSE,
    },

    {
      id: SA_TRANS_LAST_DOSE,
      type: "date_input",
      text: "Date of last dose",
      storesTo: "sharedTransferLastDoseDate",
      max: 0,
      next: SA_TRANS_START_ROUTE,
    },

    {
      id: SA_TRANS_START_ROUTE,
      type: "condition_route",
      conditions: [
        {
          // Fail safe: a missing or non-numeric BMI cannot be shown to meet the starting BMI, so it takes the below-start checks.
          evaluate: (ctx) =>
            Number.isFinite(Number(ctx.context["bmi"])) &&
            Number(ctx.context["bmi"]) >= transferStartingBmi(ctx.context["aht-sel-transfer-drug"]),
          then: SA_Q2_BMI_CONFIRM,
        },
      ],
      default: SA_TRANS_BELOW_FLAG,
    },

    {
      id: SA_TRANS_BELOW_FLAG,
      type: "flag",
      variant: "info",
      message:
        "Today's BMI is below the starting BMI for this medicine. Supply is only possible for a patient already established on it and responding: recorded for this transfer.",
      autoAdvance: true,
      auditFlags: ["transfer_below_starting_bmi_established_responding"],
      next: SA_TRANS_FLOOR_ROUTE,
    },

    {
      id: SA_TRANS_FLOOR_ROUTE,
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => {
            const floor = transferBmiFloor(ctx.context["aht-sel-transfer-drug"]);
            return floor !== null && !(Number(ctx.context["bmi"]) >= floor);
          },
          then: { outcomeId: OUT_TRANS_BELOW_FLOOR },
        },
        {
          evaluate: (ctx) => transferBmiFloor(ctx.context["aht-sel-transfer-drug"]) === null,
          then: SA_TRANS_DECISION,
        },
      ],
      default: SA_Q3_CONSENT,
    },

    {
      id: SA_TRANS_DECISION,
      type: "single_select",
      text: "Decision to continue below the starting BMI",
      hint: "This medicine has no BMI floor. Record the clinician's decision and the reason.",
      options: [
        { id: "trans_continue", label: "Continue treatment" },
        { id: "trans_stop", label: "Stop treatment" },
      ],
      routing: {
        trans_continue: SA_TRANS_DECISION_REASON,
        trans_stop: SA_TRANS_DECISION_REASON,
      },
      storesTo: "sharedTransferContinueDecision",
      required: true,
    },

    {
      id: SA_TRANS_DECISION_REASON,
      type: "free_text",
      text: "Reason for the decision",
      required: true,
      minLength: 3,
      storesTo: "sharedTransferContinueReason",
      next: "sa-trans-decision-route",
    },

    {
      id: "sa-trans-decision-route",
      type: "condition_route",
      conditions: [
        {
          evaluate: (ctx) => ctx.context["sharedTransferContinueDecision"] === "trans_continue",
          then: SA_Q3_CONSENT,
        },
      ],
      // Stop, or anything unrecognised, is not supplied.
      default: { outcomeId: OUT_TRANS_STOPPED },
    },

    // BMI threshold auto-evaluation
    // Evaluates recorded BMI against inclusion thresholds without asking
    // the pharmacist when the answer is unambiguous.
    //
    // Thresholds (AHT drug selection pathway document):
    //   ≥30.0 kg/m²                                          → meets threshold (all groups)
    //   ≥27.5 kg/m² + adjusted ethnic group                  → meets threshold
    //   27.0–29.9 kg/m² without ethnic adjustment            → ambiguous (comorbidity
    //                                                           status unknown at this
    //                                                           stage) — ask pharmacist
    //   <27.0 kg/m²                                          → does not meet threshold
    //
    // Adjusted ethnic groups: south_asian, chinese, other_asian,
    //   middle_eastern, black_african, african_caribbean
    {
      id: SA_Q2_BMI_CONFIRM,
      type: "condition_route",
      conditions: [
        {
          // BMI ≥30 — unambiguously meets threshold for all ethnic groups
          evaluate: (ctx) => Number(ctx.context["bmi"]) >= 30,
          then: SA_Q3_CONSENT,
        },
        {
          // BMI ≥27.5 with ethnic adjustment — meets threshold
          evaluate: (ctx) => {
            const bmi = Number(ctx.context["bmi"]);
            return bmi >= 27.5 && patientInAdjustedEthnicGroup(ctx.patient);
          },
          then: SA_Q3_CONSENT,
        },
        {
          // BMI <27 — unambiguously does not meet threshold
          evaluate: (ctx) => Number(ctx.context["bmi"]) < 27,
          then: { outcomeId: OUT_BMI_BELOW_THRESHOLD },
        },
        {
          // BMI 27.0–29.9 without ethnic adjustment — ambiguous.
          // Comorbidity status not yet captured at this point in the shared
          // assessment. Ask pharmacist to confirm.
          evaluate: (ctx) => {
            const bmi = Number(ctx.context["bmi"]);
            return (
              bmi >= 27 && bmi < 30 && !patientInAdjustedEthnicGroup(ctx.patient)
            );
          },
          then: SA_Q2_BMI_CONFIRM_MANUAL,
        },
      ],
      default: { outcomeId: OUT_BMI_BELOW_THRESHOLD },
      storesTo: "sharedBmiInclusionMet",
      storedValue: () => true,
    },

    // Manual BMI threshold confirmation — only shown when BMI is 27.0–29.9
    // without ethnic adjustment and comorbidity status is unknown.
    {
      id: SA_Q2_BMI_CONFIRM_MANUAL,
      type: "yes_no",
      text: "BMI is between 27.0–29.9 kg/m². Does the patient have at least one weight-related comorbidity that qualifies them for treatment? (e.g. type 2 diabetes, hypertension, dyslipidaemia, sleep apnoea, cardiovascular disease)",
      hint: "BMI ≥27 with ≥1 weight-related comorbidity meets the inclusion threshold. If no qualifying comorbidity is present, the patient does not currently meet the criteria.",
      storesTo: "sharedBmiInclusionMet",
      answerPolarity: "yes-positive",
      yes: SA_Q3_CONSENT,
      no: { outcomeId: OUT_BMI_BELOW_THRESHOLD },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 3 — Shared commitments (all must be confirmed)
    // MultiSelectNode: routing checks all 4 IDs present → proceed; else → not met
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: SA_Q3_CONSENT,
      type: "multi_select",
      text: "Confirm the following with the patient before proceeding",
      hint: "All four items must be confirmed. If any cannot be confirmed, the consultation cannot proceed.",
      items: [
        {
          id: "sa-q3-informed-consent",
          text: "Informed consent has been obtained and documented",
        },
        {
          id: "sa-q4-monitoring-consent",
          text: "Patient has consented to monthly monitoring and review",
        },
        {
          id: "sa-q5-lifestyle",
          text: "Patient has agreed to a reduced-calorie diet and increased physical activity alongside treatment",
        },
        {
          id: "sa-q6-f2f",
          text: "Patient is attending face-to-face at a registered GPhC/CQC premises",
        },
      ],
      noneOption: { label: "Patient cannot confirm one or more of these" },
      storesMany: {
        sharedInformedConsent:    (ids: string[]) => ids.includes("sa-q3-informed-consent"),
        sharedMonitoringConsent:  (ids: string[]) => ids.includes("sa-q4-monitoring-consent"),
        sharedLifestyleAgreement: (ids: string[]) => ids.includes("sa-q5-lifestyle"),
        sharedFaceToFace:         (ids: string[]) => ids.includes("sa-q6-f2f"),
      },
      routing: (selections) => {
        const required = [
          "sa-q3-informed-consent",
          "sa-q4-monitoring-consent",
          "sa-q5-lifestyle",
          "sa-q6-f2f",
        ];
        return required.every((id) => selections.includes(id))
          ? SA_Q7_BMI_FLOOR
          : { outcomeId: OUT_COMMITMENTS_NOT_MET };
      },
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Step 4 — Shared exclusion criteria (any selected exits)
    // Drug-specific exclusions are NOT assessed here.
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: SA_Q7_BMI_FLOOR,
      type: "checklist",
      text: "Does any of the following apply to this patient?",
      hint: "Select all that apply. If any exclusion criterion is present, the consultation cannot proceed. Select ‘None of the above’ if none apply.",
      items: [
        {
          id: "sa-q7a-bariatric-surgery",
          text: "Prior bariatric or metabolic surgery (e.g. gastric bypass, sleeve gastrectomy, gastric band, duodenal switch)",
        },
        {
          id: "sa-q8-pregnancy",
          text: "Patient is pregnant, breastfeeding, or planning pregnancy",
          hideIfBiologicalSex: "male",
        },
        {
          id: "sa-q9-concurrent",
          text: "Patient is currently using another weight loss medicine or GLP-1 receptor agonist for any indication",
        },
        {
          id: "sa-q10-endocrine",
          text: "Obesity is secondary to an endocrine condition currently under specialist management",
        },
        {
          id: "sa-q11-monitoring-refusal",
          text: "Patient is refusing BMI monitoring or required follow-up reviews",
        },
      ],
      includeNoneOption: true,
      noneOptionLabel: "None of the above — no exclusion criteria present",
      storesMany: {
        sharedExclusionLabels: (ids: string[]) =>
          SA_EXCLUSION_LABELS.filter(([id]) => ids.includes(id)).map(([, label]) => label),
        sharedPriorBariatricSurgery: (ids: string[]) => ids.includes("sa-q7a-bariatric-surgery"),
        sharedPregnancyBreastfeeding: (ids: string[]) => ids.includes("sa-q8-pregnancy"),
        sharedConcurrentWeightAgent: (ids: string[]) => ids.includes("sa-q9-concurrent"),
        sharedEndocrineObesity:      (ids: string[]) => ids.includes("sa-q10-endocrine"),
        sharedMonitoringRefusal:     (ids: string[]) => ids.includes("sa-q11-monitoring-refusal"),
      },
      anySelected: { outcomeId: OUT_EXCLUSION_TRIGGERED },
      noneSelected: { outcomeId: OUT_CLEARED },
    },
  ],

  outcomes: {
    [OUT_INCLUSION_NOT_MET]: {
      id: OUT_INCLUSION_NOT_MET,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot proceed — inclusion criterion not met",
      reason: "One or more shared inclusion criteria have not been met. Treatment cannot be initiated at this time.",
      safetyNetting: [
        "Discuss with the patient the reason they do not currently meet the criteria for treatment.",
        "Provide lifestyle advice including reduced-calorie diet and increased physical activity.",
        "Refer to GP if clinically appropriate.",
      ],
    },

    [OUT_BMI_BELOW_THRESHOLD]: {
      id: OUT_BMI_BELOW_THRESHOLD,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "BMI below inclusion threshold",
      reason: "The patient's recorded BMI does not meet the minimum threshold for weight management treatment under this service (BMI ≥30; or ≥27 with ≥1 weight-related comorbidity; or ≥27.5 in eligible ethnic groups). Treatment cannot be initiated at this time.",
      safetyNetting: [
        "Discuss the BMI threshold with the patient and explain why treatment cannot be initiated.",
        "Provide lifestyle advice including reduced-calorie diet and increased physical activity.",
        "Refer to GP if the patient has weight-related health concerns that may warrant specialist input.",
        "Advise the patient to return if BMI increases or if weight-related comorbidities develop.",
      ],
    },

    [OUT_BMI_IOTF_BELOW]: {
      id: OUT_BMI_IOTF_BELOW,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "BMI below IOTF threshold for adolescent eligibility",
      reason: "The patient's recorded BMI does not meet the IOTF age- and sex-specific cut-off for adolescent eligibility under this service. Liraglutide in patients aged 12–17 requires BMI at or above the IOTF obesity cut-off for their age and sex.",
      safetyNetting: [
        "Discuss the IOTF BMI threshold with the patient and their parent or guardian.",
        "Refer to GP or paediatric services for weight management support.",
        "Provide age-appropriate lifestyle advice.",
      ],
    },

    [OUT_WEIGHT_BELOW_60KG]: {
      id: OUT_WEIGHT_BELOW_60KG,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Body weight below minimum for adolescent eligibility",
      reason: "Liraglutide in patients aged 12–17 requires body weight above 60 kg. The patient's recorded weight does not meet this criterion. Treatment cannot be initiated at this time.",
      safetyNetting: [
        "Explain to the patient and their parent or guardian that liraglutide requires a minimum body weight of 60 kg.",
        "Refer to GP or paediatric services for weight management support.",
        "Advise the patient to return if body weight increases above 60 kg.",
      ],
    },

    [OUT_CONSENT_REFUSED]: {
      id: OUT_CONSENT_REFUSED,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Informed consent not obtained",
      reason: "The patient has not provided informed consent for weight management treatment. Treatment cannot be initiated without consent.",
      safetyNetting: [
        "Document that informed consent was not obtained and the reason where known.",
        "Offer the patient time to consider the treatment and return if they wish to proceed.",
        "Provide written information about the service if the patient would like to review it.",
      ],
    },

    [OUT_MONITORING_REFUSED]: {
      id: OUT_MONITORING_REFUSED,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Monitoring and follow-up requirements not accepted",
      reason: "The patient has declined to agree to the monitoring and follow-up requirements of the service. Treatment cannot be safely initiated without the patient's commitment to ongoing review.",
      safetyNetting: [
        "Explain the monitoring requirements to the patient and why they are necessary for safe treatment.",
        "Offer the patient time to consider and return if they agree to the requirements.",
        "Refer to GP if the patient has weight-related health concerns.",
      ],
    },

    [OUT_LIFESTYLE_DECLINED]: {
      id: OUT_LIFESTYLE_DECLINED,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Lifestyle commitment not confirmed",
      reason: "The patient has not confirmed commitment to the lifestyle changes required alongside weight management treatment. Pharmacological treatment alone is not appropriate without dietary and physical activity support.",
      safetyNetting: [
        "Explain that weight management medicines work best alongside dietary changes and increased physical activity.",
        "Provide lifestyle advice and signpost to local weight management services.",
        "Invite the patient to return when they are ready to commit to the full programme.",
      ],
    },

    [OUT_COMMITMENTS_NOT_MET]: {
      id: OUT_COMMITMENTS_NOT_MET,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "One or more commitment requirements not confirmed",
      reason: "Not all required commitments were confirmed. Informed consent, monitoring agreement, lifestyle commitment, and face-to-face attendance must all be confirmed before treatment can be initiated.",
      safetyNetting: [
        "Discuss reason · offer lifestyle advice · refer to GP if appropriate · document.",
      ],
    },

    [OUT_EXCLUSION_TRIGGERED]: {
      id: OUT_EXCLUSION_TRIGGERED,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot supply — shared exclusion criterion triggered",
      reasonListFrom: "sharedExclusionLabels",
      reason: "A shared exclusion criterion has been identified. Supply cannot proceed under this service.",
      safetyNetting: [
        "Document the exclusion criterion identified.",
        "Advise the patient accordingly.",
        "Refer to GP as appropriate.",
      ],
    },

    [OUT_UNDER12]: {
      id: OUT_UNDER12,
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Patient under 12 — not eligible",
      reason: "Weight management medicines under this PGD are not licensed for patients under 12 years of age. Refer to GP or paediatric services.",
      safetyNetting: [
        "Refer to GP or paediatric services for specialist weight management support.",
      ],
    },

    [OUT_OVER84]: {
      id: OUT_OVER84,
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Patient aged 85 or over — not eligible",
      reason: "Weight management medicines under this PGD are not licensed for patients aged 85 or over. Refer to GP for specialist review.",
      safetyNetting: [
        "Refer to GP for specialist weight management review.",
      ],
    },

    [OUT_CLEARED]: {
      id: OUT_CLEARED,
      type: "outcome",
      outcomeType: "chain",
      chainId: "aht-wt-shared-assessment-chain",
      title: "Shared assessment cleared",
      reason: "All shared inclusion criteria met and no shared exclusion criteria triggered. Proceed to drug-specific assessment.",
      safetyNetting: [],
    },

    [OUT_TRANS_NO_EVIDENCE]: {
      id: OUT_TRANS_NO_EVIDENCE,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Cannot transfer — no evidence of current treatment",
      reason: "A transfer needs evidence of current treatment seen by the clinician: a previous supply record, prescription, dispensing label, or the patient's medicine pack or pen.",
      safetyNetting: [
        "Ask the patient to bring the evidence and return.",
        "Alternatively, treat as a new patient if they meet the entry criteria.",
      ],
    },

    [OUT_TRANS_BELOW_FLOOR]: {
      id: OUT_TRANS_BELOW_FLOOR,
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Refer to GP — BMI below the floor for continuing treatment",
      reason: "Today's BMI is below the floor for continuing this medicine (22 kg/m² for tirzepatide; 22.5 kg/m² for the other GLP-1 type medicines). Treatment cannot continue under the PGD.",
      safetyNetting: [
        "Refer to the GP.",
        "Document the BMI and the action taken.",
      ],
    },

    [OUT_TRANS_STOPPED]: {
      id: OUT_TRANS_STOPPED,
      type: "outcome",
      outcomeType: "refer_routine",
      title: "Refer to GP — treatment stopped below the starting BMI",
      reason: "The clinician decided not to continue treatment below the starting BMI, and the reason is recorded.",
      safetyNetting: [
        "Refer to the GP.",
        "Document the decision and the reason.",
      ],
    },

    [OUT_REINIT_CANNOT]: {
      id: OUT_REINIT_CANNOT,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Re-initiation criteria not met",
      reason: "One or more re-initiation criteria could not be confirmed. Re-initiation requires: documented original BMI meeting eligibility criteria, documented ≥5% weight loss response, presentation within 12 months of last dose, documented weight regain since stopping, and current BMI above the drug-specific floor.",
      safetyNetting: [
        "Review the patient's treatment records before proceeding.",
        "If the patient's current BMI has fallen below the drug floor, re-initiation cannot proceed under this PGD.",
        "Consider referral to GP for alternative management.",
      ],
    },

    [OUT_REINIT_OVER12M]: {
      id: OUT_REINIT_OVER12M,
      type: "outcome",
      outcomeType: "cannot_supply",
      title: "Re-initiation not available — more than 12 months since last dose",
      reason: "More than 12 months have elapsed since the patient's last dose. Re-initiation under this PGD is only available within 12 months of the last dose.",
      safetyNetting: [
        "The patient must satisfy the standard eligibility criteria afresh and be treated as a new initiation.",
        "Start a new Initial Supply consultation for this patient.",
      ],
    },
  },
};
